/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from "react";

import BASELoading from "../../../../../../../@BASE/core/BASELoading";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import BASESvgIcon from "../../../../../../../@BASE/core/BASESvgIcon";

import EMentor from "../components/EMentor";
import MCInstructions from "../components/MCInstructions";
import ComponentCard from "../components/Component";
import ComponentPreview from "../components/Preview";
import ComponentStepLabel from "../components/StepLabel";
import SubComponentCard from "../components/SubComponent";

const MultipleChoice = (props) => {
  const { data, dir, ins = [], course, comments = [] } = props;
  const tl = `${dir === "rtl" ? "text-18" : "text-14"} ${course?.language?.acronym}`.trim();

  if (!data) {
    return <BASELoading />;
  }

  // filer comments by component_id
  const componentCommentsCount = (component) => {
    return comments.filter(
      (comment) =>
        comment.component === component && comment.internality !== true
    ).length;
  };

  // determine question type
  const questionType = (type) => {
    switch (type) {
      case 0: {
        return "Reading Comprehension";
      }
      case 1: {
        return "Listening Comprehension";
      }
      case 2: {
        return "Statement Completion";
      }
      case 3: {
        return "Translation Analysis";
      }
      case 4: {
        return "Skill Integration";
      }
      case 5: {
        return "Question Selection";
      }
    }
  };

  const questionInstructions = (ins, type, m) => {
    const pickInstructions = (key) => {
      return ins.filter((data) => data.label.includes(`-${key}`));
    };

    switch (type) {
      case 0: {
        if (m) {
          return pickInstructions("variation-1");
        } else {
          return pickInstructions("default");
        }
      }
      case 1: {
        return pickInstructions("default");
      }
      case 2: {
        return pickInstructions("variation-2");
      }
      case 3: {
        return pickInstructions("variation-3");
      }
      case 4: {
        if (m) {
          return pickInstructions("variation-5");
        } else {
          return pickInstructions("variation-4");
        }
      }
      case 5: {
        return pickInstructions("variation-6");
      }
    }
  };

  return (
    <>
      <style>
        {`
          .wysiwyg p {
            margin: 0px;
          }
        `}
      </style>

      {data.core.map((question, index) => (
        <ComponentCard
          key={question.id}
          title={"Question " + (index + 1)}
          id={question.id}
          commentsCount={componentCommentsCount(question.id)}
        >
          <SubComponentCard
            title="Instructions"
            id={question.id + "instructions"}
            commentsCount={componentCommentsCount(question.id + "instructions")}
          >
            <MCInstructions
              ins={questionInstructions(
                ins,
                question.type_id,
                question.multi_select
              )}
              course={course}
              dir={dir}
              tl={tl}
              componentCommentsCount={componentCommentsCount}
              qIndex={index}
            />
          </SubComponentCard>
          <SubComponentCard
            title="Authentic Material"
            id={question.id + "material"}
            commentsCount={componentCommentsCount(question.id + "material")}
          >
            <Paper elevation={0} square className="p-16">
              {question.materials.map((material, num) => (
                <Grid key={num} container spacing={1}>
                  <Grid item xs={12} sm={4} lg={2}>
                    <Typography className="text-14 font-semibold m-0">
                      Title (TL):
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} lg={10}>
                    <Typography className={`${tl} m-0`} dir={dir}>
                      {material.material_title_tl}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} lg={2}>
                    <Typography className="text-14 font-semibold m-0">
                      Title (EN):
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} lg={10}>
                    <Typography className="text-14 m-0">
                      {material.material_title_en}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} lg={2}>
                    <Typography className="text-14 font-semibold m-0">
                      Source URL:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} lg={10}>
                    <Typography className="text-14 m-0">
                      {material.source_url}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Paper>
          </SubComponentCard>
          <SubComponentCard
            title="Content"
            id={question.id + "content"}
            commentsCount={componentCommentsCount(question.id + "content")}
          >
            <Paper
              elevation={0}
              variant="outlined"
              square
              className="p-16 m-16"
            >
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Grid container spacing={0}>
                    <Grid item xs={12}>
                      <Typography className="text-14 font-bold m-0">
                        Prompt:
                      </Typography>
                      <Divider className="my-16" />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className={`${tl} m-0`} dir={dir}>
                        {question.prompt}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            <Paper
              elevation={0}
              variant="outlined"
              square
              className="p-16 m-16"
            >
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography className="text-14 font-bold m-0">
                    Lures:
                  </Typography>
                  <Divider className="my-16" />
                  <Grid container spacing={0}>
                    {question.lures.map((lure, index) => (
                      <Grid
                        key={`lure-${lure.lure_text}-${index}`}
                        item
                        xs={12}
                      >
                        {index > 0 && <Divider className="mt-2 mb-8" />}
                        <Grid container spacing={0} columns={24}>
                          <Grid item xs={4}>
                            {lure.is_correct ? (
                              <Grid container direction="row">
                                <Typography className="text-14 font-semibold m-0">
                                  Correct
                                </Typography>
                                <BASESvgIcon
                                  className="text-green-600 mx-2"
                                  size={20}
                                >
                                  heroicons-solid:check-circle
                                </BASESvgIcon>
                                <Typography className="text-14 font-semibold m-0">
                                  Lure:
                                </Typography>
                              </Grid>
                            ) : (
                              <Grid container direction="row">
                                <Typography className="text-14 font-semibold m-0">
                                  Incorrect
                                </Typography>
                                <BASESvgIcon
                                  className="text-red-600 mx-2"
                                  size={20}
                                >
                                  heroicons-solid:x-circle
                                </BASESvgIcon>
                                <Typography className="text-14 font-semibold m-0">
                                  Lure:
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                          <Grid item xs>
                            <Typography
                              className={`${tl} m-0 ${
                                index < question.lures.length - 1 && `mb-8`
                              }`}
                              dir={dir}
                            >
                              {lure.lure_text}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            <Paper
              elevation={0}
              variant="outlined"
              square
              className="p-16 m-16"
            >
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Grid container spacing={0}>
                    <Grid item xs={12}>
                      <Typography className="text-14 font-bold m-0">
                        Correct Feedback:
                      </Typography>
                      <Divider className="my-16" />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        className={`${tl} m-0 wysiwyg`}
                        dir={dir}
                        dangerouslySetInnerHTML={{
                          __html: question.correct_feedback,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            <Paper
              elevation={0}
              variant="outlined"
              square
              className="p-16 m-16"
            >
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Grid container spacing={0}>
                    <Grid item xs={12}>
                      <Typography className="text-14 font-bold m-0">
                        First Incorrect Feedback:
                      </Typography>
                      <Divider className="my-16" />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        className={`${tl} m-0 wysiwyg`}
                        dir={dir}
                        dangerouslySetInnerHTML={{
                          __html: question.incorrect_feedback_1,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            <Paper
              elevation={0}
              variant="outlined"
              square
              className="p-16 m-16"
            >
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Grid container spacing={0}>
                    <Grid item xs={12}>
                      <Typography className="text-14 font-bold m-0">
                        Second Incorrect Feedback:
                      </Typography>
                      <Divider className="my-16" />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        className={`${tl} m-0 wysiwyg`}
                        dir={dir}
                        dangerouslySetInnerHTML={{
                          __html: question.incorrect_feedback_2,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            <Paper
              elevation={0}
              variant="outlined"
              square
              className="p-16 m-16"
            >
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Grid container spacing={0}>
                    <Grid item xs={12}>
                      <Typography className="text-14 font-bold m-0">
                        Final Incorrect Feedback:
                      </Typography>
                      <Divider className="my-16" />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        className={`${tl} m-0 wysiwyg`}
                        dir={dir}
                        sx={{ margin: "0" }}
                        dangerouslySetInnerHTML={{
                          __html: question.incorrect_feedback_3,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            <Paper
              elevation={0}
              variant="outlined"
              square
              className="p-16 m-16"
            >
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography className="text-14 font-bold m-0">
                    Question Options:
                  </Typography>
                  <Divider className="my-16" />
                  <Grid container spacing={1}>
                    <Grid item xs={4} sm={3}>
                      <Typography className="text-14 font-semibold m-0">
                        Maximum Number of Tries:
                      </Typography>
                      <Typography className="text-14 font-semibold m-0">
                        Multiple Select:
                      </Typography>
                    </Grid>
                    <Grid item xs={8} sm={9}>
                      <Typography className="text-14 m-0">
                        {question.max_number_of_tries}
                      </Typography>
                      <Typography className="text-14 m-0">
                        {question.multi_select ? "true" : "false"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </SubComponentCard>
        </ComponentCard>
      ))}
      {data.eMentor.length !== 0 && (
        <EMentor
          eMentor={data.eMentor}
          tl={tl}
          dir={dir}
          componentCommentsCount={componentCommentsCount}
        />
      )}
    </>
  );
};

export default MultipleChoice;
